<template>

        <group-report-template  v-model:for-group="forGroup"
                                v-model:interval="$data._interval"
                                v-model:reload-data="$data._reloadTable"
                                v-model:running-errors="$data._errorSending"
                                v-model:ran-report="$data._reportSuccessfullySent"
                                v-model:running-report="$data._runningReport"
                                v-model:current-report-template-id="$data._reportTemplateId"
                                v-model:display-section="displaySection"
                                v-model:settings="$data._settings"
                                :default-settings="defaultSettings"
                                :can-edit="permissions.can('run_temperature_interval_breakdown_report')"
                                :hide-right-column=true
                                :hide-title=true
                                report-name="temperature-interval-breakdown-report"
                                @useStandardReporting="setUseStandardReporting"
        >

            <template #report-title>
                <div class="flex flex-col">
                    <div class="mb-2 flex items-center space-x-2 sm:space-x-3">
                        <div class="flex h-8 w-8 items-center justify-center rounded-md bg-blue-500 text-white">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-temperature">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                <path d="M10 13.5a4 4 0 1 0 4 0v-8.5a2 2 0 0 0 -4 0v8.5" />
                                <path d="M10 9l4 0" />
                            </svg>
                        </div>
                        <h1 class="mb-0 p-0 text-left text-xl font-bold text-gray-900 sm:text-3xl">
                            Temperature Interval Breakdown Report
                        </h1>
                    </div>
                    <p class="text-sm text-gray-600 sm:text-base">See how monitored points spend time across defined temperature ranges. This report breaks down time above thresholds into customizable intervals, providing clear insight into temperature patterns and performance.</p>
                </div>
            </template>

            <template #run-options>
                <div class="space-y-4">

                    <div class="mt-0 space-y-2">
                        <label for="meters" class="text-sm text-gray-500">
                            Meters
                        </label>
                        <select2
                            v-model:value="selectedMeters"
                            :options="meters"
                            :multiple="true"
                            class="select"
                        ></select2>
                        <p v-if="errors.selectedMeters" class="text-xs text-red-500 mt-2">{{ errors.selectedMeters }}</p>
                    </div>

                    <div class="flex w-full">
                        <div class="flex w-full flex-col">
                            <label for="startDate" class="ml-1 text-sm text-gray-500">Start Date</label>
                            <date-time-picker id="startDate"
                                              v-model="startDate"
                                              :min-date=minDate
                                              :max-date=maxDate
                                              format="YYYY-MM-DD HH:mm"
                                              :side-by-side="true"
                            >
                            </date-time-picker>
                            <p v-if="errors.startDate" class="text-xs text-red-500 mt-2">{{ errors.startDate }}</p>
                        </div>

                        <div class="flex w-full flex-col">
                            <label for="endDate" class="ml-1 text-sm text-gray-500">End Date</label>
                            <date-time-picker id="endDate"
                                              v-model="endDate"
                                              :min-date=minDate
                                              :max-date=maxDate
                                              format="YYYY-MM-DD HH:mm"
                                              :side-by-side="true"
                            >
                            </date-time-picker>
                            <p v-if="errors.endDate" class="text-xs text-red-500 mt-2">{{ errors.endDate }}</p>
                        </div>
                    </div>

                    <div class="space-y-2">
                        <label class="text-sm text-gray-500">
                            Temperature Analysis Type
                        </label>
                        <div class="pl-2 space-y-3">
                            <div class="flex items-start">
                                <input id="interval-1"
                                       v-model="intervalType"
                                       name="interval_type"
                                       type="radio"
                                       value="1_degree"
                                       class="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                >
                                <label for="interval-1" class="ml-3 block text-sm text-gray-600">
                                    1 Degree Intervals
                                    <span class="block text-xs text-gray-500">Break down temperature ranges in 1°F increments above the alarm threshold</span>
                                </label>
                            </div>
                            <div class="flex items-start">
                                <input id="interval-5"
                                       v-model="intervalType"
                                       name="interval_type"
                                       type="radio"
                                       value="5_degree"
                                       class="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                >
                                <label for="interval-5" class="ml-3 block text-sm text-gray-600">
                                    5 Degree Intervals
                                    <span class="block text-xs text-gray-500">Break down temperature ranges in 5°F increments above the high alarm threshold</span>
                                </label>
                            </div>
                            <div class="flex items-start">
                                <input id="interval-single"
                                       v-model="intervalType"
                                       name="interval_type"
                                       type="radio"
                                       value="single_temperature"
                                       class="mt-1 h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                >
                                <label for="interval-single" class="ml-3 block text-sm text-gray-600">
                                    Single Temperature Analysis
                                    <span class="block text-xs text-gray-500">Analyze time spent at a specific temperature</span>
                                </label>
                            </div>
                            <p v-if="errors.intervalType" class="text-xs text-red-500 mt-2">{{ errors.intervalType }}</p>
                        </div>
                    </div>

                    <div v-if="intervalType === 'single_temperature'" id="single-temp-input" class="space-y-2">
                        <label for="target_temperature" class="text-sm text-gray-500">
                            Target Temperature
                        </label>
                        <input type="number"
                               id="target_temperature"
                               v-model="targetTemperature"
                               name="target_temperature"
                               required
                               class="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                        <p v-if="errors.targetTemperature" class="text-xs text-red-500 mt-2">{{ errors.targetTemperature }}</p>
                    </div>

                    <div class="mt-0 flex justify-end pt-4">
                        <button class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-blue-600 focus:border-blue-700 focus:outline-none focus:ring-indigo-200 active:bg-blue-700 sm:max-w-80"
                                @click="runReport(_reportTemplateId)"
                        >
                            <span>Generate Report</span>
                        </button>
                    </div>
                </div>
            </template>

        </group-report-template>

</template>

<script>
import axios from "axios"
import Select2 from "@/components/elements/select2.vue"
import DateTimePicker from "@/components/Reports/VueDatePicker.vue"
import GroupReportTemplate from "@/components/GroupReports/GroupReportTemplate.vue";
import { SonicuCoreMixin } from "@/mixins/SonicuCoreMixin"

export default {
    name: "TemperatureIntervalBreakdownReport",
    components: {GroupReportTemplate, Select2, DateTimePicker},
    mixins: [SonicuCoreMixin],
    props: {
        meters: {
            type: Object,
            default: () => ({}),
        },
        minDate: {
            type: [Date, String],
            default: () => new Date(),
        },
        maxDate: {
            type: [Date, String],
            default: () => new Date(),
        },
        timezone: {
            type: String,
            default: "America/New_York",
        },
        interval: {
            type: String,
            default: "monthly",
        },
        defaultSettings: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            selectedMeters: [],
            startDate: new Date(new Date().setHours(0, 0, 0, 0)),
            endDate: new Date(),
            intervalType: null,
            targetTemperature: null,
            displaySection: null,

            // Manual Run Stuff
            _startOfInterval: new Date(),
            _endOfInterval: new Date(),
            format: ["monthly", "semi-monthly"].indexOf(this.interval) > -1 ? "MMMM YYYY" : "MMMM DD, YYYY",
            viewMode: ["monthly", "semi-monthly"].indexOf(this.interval) > -1 ? "months" : "days",
            isRunning: false,

            _interval: this.interval,
            _selectedMeters: [],
            _templateName: "",
            _groupId: null,
            _runningReport: false,
            _reportSuccessfullySent: false,
            _errorSending: [],
            _reportTemplateId: null,

            // Template Run Stuff
            headers: [
                {
                    name: "Report Template",
                    key: "template_name",
                    sortBy: "reportTemplate",
                },
                {
                    name: "Send Interval",
                    key: "report_duration_type",
                    sortBy: "sendInterval",
                },
                {
                    name: "",
                    key: "created_at",
                },
            ],
            data: [],

            _reloadTable: false,
            _settings: this.defaultSettings,

            currentReportTemplateIndex: null,
            showIntervalSelectionPanel: false,
            showCreateTemplatePanel: false,
            showEditTemplatePanel: false,
            forGroup: false,
            useStandardReporting: false,

            errors: {
                selectedMeters: null,
                startDate: null,
                endDate: null,
                intervalType: null,
                targetTemperature: null
            },
        }
    },
    methods: {
        runReport(reportTemplateId = null) {
            // Build params including all relevant form parameters
            let params = {};
            if (reportTemplateId !== null) {
                params["reportTemplateId"] = reportTemplateId;
                params["forGroup"] = this.forGroup;
            }
            params["selected_meters"] = this.selectedMeters;
            params["start_date"] = this.startDate;
            params["end_date"] = this.endDate;
            params["interval_type"] = this.intervalType;
            if (this.intervalType === 'single_temperature') {
                params["target_temperature"] = this.targetTemperature;
            }

            this._runningReport = true
            axios
                .post(this.company.buildCompanyRoute("group-reports/temperature-interval-breakdown"), params)
                .then((response) => {
                    let dataRes = response.data
                    this._reportSuccessfullySent = true
                })
                .catch((error) => {
                    const dataRes = error.response?.data
                    this._errorSending = dataRes

                    this.errors = {
                        selectedMeters: null,
                        startDate: null,
                        endDate: null,
                        intervalType: null,
                        targetTemperature: null
                    }

                    if (dataRes?.errors) {
                        if (dataRes.errors.selected_meters) {
                            this.errors.selectedMeters = dataRes.errors.selected_meters[0]
                        }
                        if (dataRes.errors.startDate) {
                            this.errors.startDate = dataRes.errors.startDate[0]
                        }
                        if (dataRes.errors.endDate) {
                            this.errors.endDate = dataRes.errors.endDate[0]
                        }
                        if (dataRes.errors.interval_type) {
                            this.errors.intervalType = dataRes.errors.interval_type[0]
                        }
                        if (dataRes.errors.target_temperature) {
                            this.errors.targetTemperature = dataRes.errors.target_temperature[0]
                        }
                    }

                    console.error("Report failed to queue", dataRes)

                    if (dataRes?.errors) {
                        console.error("Validation errors:", dataRes.errors)
                    }
                })
                .finally(() => {
                    this._runningReport = false
                })
        },
        resetTemplateRun() {
            this.currentReportTemplateIndex = null
            this.showIntervalSelectionPanel = false
            this.showEditTemplatePanel = false
            this.showCreateTemplatePanel = false
            this.forGroup = false
        },
        setUseStandardReporting(event) {
            this.useStandardReporting = event
        },
    },
    watch: {
        showIntervalSelectionPanel(val, oldVal) {
            if (val !== oldVal && val === false) {
                this.resetTemplateRun()
            }
        },
        showEditTemplatePanel(val, oldVal) {
            if (val !== oldVal && val === false) {
                this.resetTemplateRun()
            }
        },
        _settings(val, oldVal) {
            if (val !== oldVal) {
                this._settings = val
            }
        },
        defaultSettings(val, oldVal) {
            if (val !== oldVal) {
                this._settings = val
            }
        },
    },
}
</script>